/**
 * Session-presence marker.
 *
 * Phase 2 of #386 moves the refresh token into an HttpOnly cookie, which is
 * the point — script on the page can no longer read it. But two places used
 * `storageGet('refresh_token')` as a *question*, not to obtain the secret:
 *
 *   - `ensureFreshToken` asks "is there a session worth refreshing?" so that a
 *     cold load with no access token refreshes once up front rather than
 *     letting a fan-out of requests all 401 and race on rotation.
 *   - the refresh path asks "is it even worth calling the endpoint?"
 *
 * This marker answers that question without holding anything secret. It says
 * only that a login happened and has not been ended; it grants nothing, and
 * forging it achieves nothing beyond one refresh request that returns 401.
 *
 * Contains: markSessionActive, clearSessionMarker, hasActiveSession.
 */

import { storageGet, storageRemove, storageSet } from './storage';

const SESSION_MARKER_KEY = 'session_active';

/** Record that a login succeeded. Carries no token. */
export function markSessionActive(): void {
  storageSet(SESSION_MARKER_KEY, '1', 'local');
}

/** Forget the session — on logout, or when a refresh is refused. */
export function clearSessionMarker(): void {
  storageRemove(SESSION_MARKER_KEY, 'local');
}

/**
 * Whether a login happened that has not been ended.
 *
 * A hint, not an authority: the cookie may still have expired server-side.
 * Its only job is to decide whether attempting a refresh is worthwhile.
 */
export function hasActiveSession(): boolean {
  return !!storageGet(SESSION_MARKER_KEY, 'local');
}
